import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { supabase } from '@/integrations/supabase/client';
import { useTenantStore } from '@/stores/tenantStore';
import { useTenantPath } from '@/hooks/useTenantPath';
import {
  fetchOrders,
  payOrderCash,
  refundOrder,
  updateOrderStatus,
  type OrderSummary,
  type OrderStatus,
} from '@/lib/api/orders';
import {
  getDiscountApprovalDetail,
  submitDiscountApproval,
  applyDiscountApprovalDecision,
  cancelDiscountApproval,
} from '@/lib/api/discountApprovals';
import type { DiscountApprovalRequestDetail } from '@/lib/api/discountApprovals';
import { ORDER_STATUS_SEQUENCE } from '@/types/orderStatus';
import {
  ShoppingBag,
  Clock,
  DollarSign,
  CreditCard,
  Search,
  Filter,
  CheckCircle,
  AlertTriangle,
  RefreshCcw,
  ChevronLeft,
  ChevronRight,
  Smartphone,
  Banknote,
  Loader2,
  Download,
  ExternalLink,
  X,
  Ban,
  RotateCcw,
  MoreHorizontal,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useExportJob } from '@/hooks/useExportJob';
import { BackButton } from '@/components/BackButton';
import { useTranslation } from 'react-i18next';
import { OrdersViewToggle } from '@/features/restaurant/components/orders/OrdersViewToggle';
import {
  DiscountApprovalSummaryCard,
  DiscountApprovalTimeline,
} from '@/features/restaurant/components/discount-approvals';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const STATUS_OPTIONS: Array<OrderStatus | 'ALL'> = ['ALL', ...ORDER_STATUS_SEQUENCE];

const EDITABLE_ORDER_STATUSES = new Set<OrderStatus>(['PENDING_PAYMENT', 'PAID', 'ACCEPTED', 'IN_PROGRESS']);
const CANCEL_BLOCKED_STATUSES = new Set<OrderStatus>(['CANCELLED', 'COMPLETED', 'VOIDED', 'REFUNDED']);

const STATUS_BADGE_STYLES: Record<OrderStatus, string> = {
  PENDING_PAYMENT:
    'border border-transparent ring-1 ring-inset ring-amber-400/35 bg-amber-400/10 text-amber-600 dark:ring-amber-400/30 dark:bg-amber-500/15 dark:text-amber-200',
  PAID:
    'border border-transparent ring-1 ring-inset ring-sky-400/35 bg-sky-400/10 text-sky-700 dark:ring-sky-500/30 dark:bg-sky-500/15 dark:text-sky-200',
  ACCEPTED:
    'border border-transparent ring-1 ring-inset ring-sky-500/35 bg-sky-500/10 text-sky-600 dark:ring-sky-500/30 dark:bg-sky-500/15 dark:text-sky-200',
  IN_PROGRESS:
    'border border-transparent ring-1 ring-inset ring-indigo-500/35 bg-indigo-500/10 text-indigo-600 dark:ring-indigo-500/25 dark:bg-indigo-500/15 dark:text-indigo-200',
  READY:
    'border border-transparent ring-1 ring-inset ring-emerald-500/35 bg-emerald-500/10 text-emerald-600 dark:ring-emerald-500/30 dark:bg-emerald-500/15 dark:text-emerald-200',
  SERVED:
    'border border-transparent ring-1 ring-inset ring-gray-300/45 bg-gray-100/60 text-gray-600 dark:ring-gray-700/50 dark:bg-gray-900/40 dark:text-gray-200',
  PICKED_UP:
    'border border-transparent ring-1 ring-inset ring-gray-300/45 bg-gray-100/60 text-gray-600 dark:ring-gray-700/50 dark:bg-gray-900/40 dark:text-gray-200',
  COMPLETED:
    'border border-transparent ring-1 ring-inset ring-gray-400/40 bg-gray-200/40 text-gray-700 dark:ring-gray-600/45 dark:bg-gray-900/45 dark:text-gray-200',
  CANCELLED:
    'border border-transparent ring-1 ring-inset ring-rose-500/35 bg-rose-500/10 text-rose-600 dark:ring-rose-500/30 dark:bg-rose-500/15 dark:text-rose-200',
  REFUNDED:
    'border border-transparent ring-1 ring-inset ring-rose-500/35 bg-rose-500/10 text-rose-600 dark:ring-rose-500/30 dark:bg-rose-500/15 dark:text-rose-200',
  VOIDED:
    'border border-transparent ring-1 ring-inset ring-rose-500/35 bg-rose-500/10 text-rose-600 dark:ring-rose-500/30 dark:bg-rose-500/15 dark:text-rose-200',
};

const PAYMENT_BADGE_STYLES: Record<string, string> = {
  PAID:
    'border border-transparent ring-1 ring-inset ring-emerald-500/35 bg-emerald-500/10 text-emerald-600 dark:ring-emerald-500/30 dark:bg-emerald-500/15 dark:text-emerald-200',
  UNPAID:
    'border border-transparent ring-1 ring-inset ring-amber-500/35 bg-amber-500/10 text-amber-700 dark:ring-amber-500/30 dark:bg-amber-500/15 dark:text-amber-200',
  REQUIRES_ACTION:
    'border border-transparent ring-1 ring-inset ring-amber-500/35 bg-amber-500/10 text-amber-700 dark:ring-amber-500/30 dark:bg-amber-500/15 dark:text-amber-200',
  FAILED:
    'border border-transparent ring-1 ring-inset ring-rose-500/35 bg-rose-500/10 text-rose-600 dark:ring-rose-500/30 dark:bg-rose-500/15 dark:text-rose-200',
  REFUNDED:
    'border border-transparent ring-1 ring-inset ring-rose-500/35 bg-rose-500/10 text-rose-600 dark:ring-rose-500/30 dark:bg-rose-500/15 dark:text-rose-200',
  VOIDED:
    'border border-transparent ring-1 ring-inset ring-gray-300/45 bg-gray-100/50 text-gray-600 dark:ring-gray-700/50 dark:bg-gray-900/40 dark:text-gray-300',
};

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  PAID: 'Paid',
  UNPAID: 'Unpaid',
  REQUIRES_ACTION: 'Action needed',
  FAILED: 'Payment failed',
  REFUNDED: 'Refunded',
  VOIDED: 'Voided',
};

const EMPTY_ORDERS: OrderSummary[] = [];
const EMPTY_METRICS = Object.freeze({
  totalOrdersToday: 0,
  revenueToday: 0,
  activeOrders: 0,
  avgOrder: 0,
});
const PAGE_SIZE_OPTIONS = [20, 50, 100] as const;

const SOFT_CARD_SURFACE =
  'rounded-3xl border border-gray-200/60 bg-gradient-to-br from-white via-gray-50/70 to-white shadow-lg shadow-gray-500/10 dark:border-gray-700/60 dark:from-gray-900/85 dark:via-gray-800/70 dark:to-gray-900/80';
const SOFT_SECTION_HEADER =
  'border-b border-gray-200/60 bg-gradient-to-r from-white/80 to-transparent dark:border-gray-700/60 dark:from-gray-900/40 dark:to-transparent';
const SOFT_OUTLINE_BADGE =
  'border border-transparent ring-1 ring-inset ring-gray-200/60 bg-gradient-to-r from-white/85 to-gray-50/70 text-gray-500 dark:ring-gray-700/60 dark:from-gray-900/50 dark:to-gray-900/20 dark:text-gray-200';
const SOFT_TABLE_SURFACE =
  'rounded-2xl border border-gray-200/60 bg-gradient-to-br from-white/95 via-white to-gray-50/80 dark:border-gray-700/60 dark:from-gray-900/85 dark:via-gray-800/70 dark:to-gray-900/80';

const currencyFormatter = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'USD',
});

type ExportFormat = 'csv' | 'json';

function formatCurrency(cents?: number | null, currency = 'USD'): string {
  if (typeof cents !== 'number') {
    return currencyFormatter.format(0);
  }
  const formatter = currency === 'USD' ? currencyFormatter : new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
  });
  return formatter.format(cents / 100);
}

const getRefundableCents = (order?: OrderSummary | null): number => {
  if (!order) {
    return 0;
  }
  if (typeof order.netPaidCents === 'number' && Number.isFinite(order.netPaidCents)) {
    return Math.max(0, Math.round(order.netPaidCents));
  }
  const collected =
    typeof order.collectedCents === 'number' && Number.isFinite(order.collectedCents)
      ? Math.max(0, Math.round(order.collectedCents))
      : 0;
  const refunded =
    typeof order.refundedCents === 'number' && Number.isFinite(order.refundedCents)
      ? Math.max(0, Math.round(order.refundedCents))
      : 0;
  return Math.max(0, collected - refunded);
};

const getOrderTotalCents = (order?: OrderSummary | null): number => {
  if (!order) {
    return 0;
  }
  if (order.totals && typeof order.totals.totalCents === 'number' && Number.isFinite(order.totals.totalCents)) {
    return Math.max(0, Math.round(order.totals.totalCents));
  }
  if (typeof order.totalCents === 'number' && Number.isFinite(order.totalCents)) {
    return Math.max(0, Math.round(order.totalCents));
  }
  return 0;
};

const getRefundDueCents = (order?: OrderSummary | null): number => {
  if (!order) {
    return 0;
  }
  if (typeof order.refundDueCents === 'number' && Number.isFinite(order.refundDueCents)) {
    return Math.max(0, Math.round(order.refundDueCents));
  }
  const totalCents = getOrderTotalCents(order);
  return Math.max(0, getRefundableCents(order) - totalCents);
};

const OrdersPageV3 = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('orders');
  const { activeRestaurantId } = useTenantStore();
  const navigate = useNavigate();
  const { tenantPath } = useTenantPath();

  const [statusFilter, setStatusFilter] = useState<OrderStatus | 'ALL'>('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');
  const [hasManualViewModeSelection, setHasManualViewModeSelection] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<OrderSummary | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE_OPTIONS[0]);
  const [pageIndex, setPageIndex] = useState(0);
  const [decisionDialog, setDecisionDialog] = useState<{
    requestId: string;
    stepId: string;
    decision: 'approve' | 'decline';
  } | null>(null);
  const [decisionNote, setDecisionNote] = useState('');
  const [decisionNoteError, setDecisionNoteError] = useState<string | null>(null);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [submitNote, setSubmitNote] = useState('');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelDialogOrder, setCancelDialogOrder] = useState<OrderSummary | null>(null);
  const [refundDialogOrder, setRefundDialogOrder] = useState<OrderSummary | null>(null);
  const [refundReason, setRefundReason] = useState('');
  const [cancelPaymentAction, setCancelPaymentAction] = useState<'refund' | 'retain'>('refund');
  const [cancellingOrderId, setCancellingOrderId] = useState<string | null>(null);
  const [refundingOrderId, setRefundingOrderId] = useState<string | null>(null);
  const counterPosPath = useMemo(() => tenantPath('sales/new?source=counter'), [tenantPath]);
  const counterPeekUrl = useMemo(() => tenantPath('sales/new/peek?source=counter&view=peek'), [tenantPath]);
  const [counterPeekOpen, setCounterPeekOpen] = useState(false);
  const [counterPeekKey, setCounterPeekKey] = useState(0);

  const ordersQuery = useQuery({
    queryKey: [
      'orders',
      'list',
      {
        restaurantId: activeRestaurantId,
        status: statusFilter,
        page: pageIndex,
        pageSize,
        search: searchTerm.trim(),
      },
    ],
    queryFn: ({ signal }) =>
      fetchOrders(
        {
          status: statusFilter === 'ALL' ? undefined : statusFilter,
          sort: 'desc',
          page: pageIndex + 1,
          pageSize,
          search: searchTerm.trim() || undefined,
        },
        signal,
      ),
    enabled: Boolean(activeRestaurantId),
    keepPreviousData: true,
    staleTime: 5_000,
  });

  const orders = ordersQuery.data?.orders ?? EMPTY_ORDERS;
  const refetchOrders = ordersQuery.refetch;
  
  // Memoized translation strings to avoid dependency on t function
  const translationStrings = useMemo(() => ({
    justNow: t('relativeTime.justNow'),
    lastUpdatedTemplate: t('metadata.lastUpdated'),
  }), [t]);
  
  const lastUpdatedLabel = useMemo(() => {
    const timeLabel = ordersQuery.dataUpdatedAt
      ? new Date(ordersQuery.dataUpdatedAt).toLocaleTimeString()
      : translationStrings.justNow;
    return translationStrings.lastUpdatedTemplate.replace('{{time}}', timeLabel);
  }, [ordersQuery.dataUpdatedAt, translationStrings]);

  const metrics = ordersQuery.data?.metrics ?? EMPTY_METRICS;

  const totalCount = ordersQuery.data?.total ?? orders.length;

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia('(max-width: 1023px)');

    const applyResponsiveMode = (matches: boolean) => {
      if (hasManualViewModeSelection) {
        return;
      }
      setViewMode((current) => {
        const target = matches ? 'cards' : 'table';
        return current === target ? current : target;
      });
    };

    applyResponsiveMode(mediaQuery.matches);

    const listener = (event: MediaQueryListEvent) => {
      applyResponsiveMode(event.matches);
    };

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', listener);
    } else {
      mediaQuery.addListener(listener);
    }

    return () => {
      if (typeof mediaQuery.removeEventListener === 'function') {
        mediaQuery.removeEventListener('change', listener);
      } else {
        mediaQuery.removeListener(listener);
      }
    };
  }, [hasManualViewModeSelection]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleCounterSuccess = (event: MessageEvent) => {
      if (event.origin && event.origin !== window.location.origin) {
        return;
      }
      const data = event.data as { type?: string; payload?: { code?: string | null } } | undefined;
      if (!data || data.type !== 'counter-pos:order-success') {
        return;
      }
      setCounterPeekOpen(false);
      const code = data.payload?.code;
      toast({
        title: t('toasts.counterSuccess.title', { defaultValue: 'Counter order submitted' }),
        description: code
          ? t('toasts.counterSuccess.descriptionWithCode', {
              code,
              defaultValue: 'Order {{code}} is now live.',
            })
          : t('toasts.counterSuccess.description', { defaultValue: 'Order is now live.' }),
      });
      refetchOrders();
    };

    window.addEventListener('message', handleCounterSuccess);
    return () => window.removeEventListener('message', handleCounterSuccess);
  }, [refetchOrders, t, toast]);

  const handleViewModeChange = useCallback(
    (mode: 'table' | 'cards') => {
      setHasManualViewModeSelection(true);
      setViewMode(mode);
    },
    [],
  );
  const currentPageSize = ordersQuery.data?.pageSize ?? pageSize;
  const currentPage = ordersQuery.data?.page ?? pageIndex + 1;
  const totalPages = ordersQuery.data?.totalPages ?? Math.max(1, Math.ceil(totalCount / Math.max(currentPageSize, 1)));
  const currentPageIndex = currentPage - 1;
  const hasNextPage = ordersQuery.data?.hasNextPage ?? currentPage < totalPages;
  const hasPreviousPage = currentPageIndex > 0;
  const pageRangeStart = totalCount === 0 ? 0 : (currentPage - 1) * currentPageSize + 1;
  const pageRangeEnd = totalCount === 0 ? 0 : Math.min(totalCount, pageRangeStart + orders.length - 1);
  const summaryText = totalCount > currentPageSize
    ? t('sections.controls.summaryPaged', { from: pageRangeStart, to: pageRangeEnd, total: totalCount })
    : t('sections.controls.summary', { filtered: orders.length, total: totalCount });

  const [pendingOrderId, setPendingOrderId] = useState<string | null>(null);
  const [cashSettlementOrderId, setCashSettlementOrderId] = useState<string | null>(null);
  const approvalRequestId = selectedOrder?.discount?.approvalRequestId ?? null;

  const approvalDetailQuery = useQuery({
    queryKey: ['discount-approval', approvalRequestId],
    queryFn: ({ signal }) => getDiscountApprovalDetail(approvalRequestId!, signal),
    enabled: detailsOpen && Boolean(approvalRequestId),
    refetchInterval: (query) => {
      const detail = query.state.data as DiscountApprovalRequestDetail | undefined;
      if (!detail) {
        return 5_000;
      }
      return ['approved', 'declined', 'cancelled', 'expired'].includes(detail.request.status)
        ? false
        : 5_000;
    },
  });

  const approvalDecisionMutation = useMutation({
    mutationFn: ({
      requestId,
      stepId,
      decision,
      note,
    }: {
      requestId: string;
      stepId: string;
      decision: 'approve' | 'decline';
      note?: string;
    }) => applyDiscountApprovalDecision(requestId, stepId, { decision, note }),
    onSuccess: (detail, variables) => {
      queryClient.setQueryData(['discount-approval', variables.requestId], detail);
      queryClient.invalidateQueries({ queryKey: ['orders', 'list'] });
      toast({
        title: variables.decision === 'approve' ? 'Approval recorded' : 'Decision recorded',
        description:
          variables.decision === 'approve'
            ? 'Discount approved successfully.'
            : 'Approval decision saved.',
      });
      setDecisionDialog(null);
      setDecisionNote('');
      setDecisionNoteError(null);
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Try again in a moment.';
      toast({
        title: 'Unable to update approval',
        description: message,
        variant: 'destructive',
      });
    },
  });

  const approvalSubmitMutation = useMutation({
    mutationFn: ({ requestId, note }: { requestId: string; note?: string }) =>
      submitDiscountApproval(requestId, note ? { note } : {}),
    onSuccess: (detail, variables) => {
      queryClient.setQueryData(['discount-approval', variables.requestId], detail);
      queryClient.invalidateQueries({ queryKey: ['orders', 'list'] });
      toast({ title: 'Request submitted', description: 'Approval workflow kicked off.' });
      setSubmitDialogOpen(false);
      setSubmitNote('');
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Try again in a moment.';
      toast({
        title: 'Unable to submit for approval',
        description: message,
        variant: 'destructive',
      });
    },
  });

  const approvalCancelMutation = useMutation({
    mutationFn: ({ requestId, reason }: { requestId: string; reason?: string }) =>
      cancelDiscountApproval(requestId, reason ? { reason } : {}),
    onSuccess: (detail, variables) => {
      queryClient.setQueryData(['discount-approval', variables.requestId], detail);
      queryClient.invalidateQueries({ queryKey: ['orders', 'list'] });
      toast({ title: 'Approval cancelled', description: 'The discount approval was cancelled.' });
      setCancelDialogOpen(false);
      setCancelReason('');
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Try again in a moment.';
      toast({
        title: 'Unable to cancel approval',
        description: message,
        variant: 'destructive',
      });
    },
  });

  const approvalDetail = approvalDetailQuery.data;
  const pendingApprovalStep =
    approvalDetail?.steps.find((step) => step.status === 'pending' || step.status === 'waiting') ?? null;
  const approvalStatus = approvalDetail?.request.status ?? null;
  const canSubmitApproval = approvalStatus === 'pending';
  const canCancelApproval =
    approvalStatus !== null && !['approved', 'declined', 'cancelled', 'expired'].includes(approvalStatus);
  const declineNoteRequired = approvalDetail?.workflow?.requireReasonCode ?? false;

  useEffect(() => {
    if (!ordersQuery.isFetching) {
      const remotePageIndex = (ordersQuery.data?.page ?? 1) - 1;
      if (remotePageIndex >= 0 && remotePageIndex !== pageIndex) {
        setPageIndex(remotePageIndex);
      }
    }
  }, [ordersQuery.data?.page, ordersQuery.isFetching, pageIndex]);

  useEffect(() => {
    setPageIndex(0);
  }, [searchTerm, statusFilter, pageSize]);

  useEffect(() => {
    const remotePageSize = ordersQuery.data?.pageSize;
    if (!ordersQuery.isFetching && typeof remotePageSize === 'number' && remotePageSize > 0 && remotePageSize !== pageSize) {
      setPageSize(remotePageSize);
    }
  }, [ordersQuery.data?.pageSize, ordersQuery.isFetching, pageSize]);

  const formatRelativeTime = useCallback(
    (iso?: string | null) => {
      if (!iso) {
        return t('relativeTime.justNow');
      }
      const timestamp = Date.parse(iso);
      if (Number.isNaN(timestamp)) {
        return t('relativeTime.justNow');
      }
      const diffMs = Date.now() - timestamp;
      const minutes = Math.floor(diffMs / 60000);
      if (minutes < 1) {
        return t('relativeTime.justNow');
      }
      if (minutes < 60) {
        return t('relativeTime.minutes', { count: minutes });
      }
      const hours = Math.floor(minutes / 60);
      if (hours < 24) {
        return t('relativeTime.hours', { count: hours });
      }
      const days = Math.floor(hours / 24);
      return t('relativeTime.days', { count: days });
    },
    [t],
  );

  const resolveOrderAdvanceTarget = (order: OrderSummary): OrderStatus | null => {
  const type = (order.type ?? '').toLowerCase();

  switch (order.status) {
    case 'READY':
      return type === 'takeaway' || type === 'delivery' ? 'PICKED_UP' : 'SERVED';
    case 'PICKED_UP':
    case 'SERVED':
      return 'COMPLETED';
    case 'PAID':
      return 'ACCEPTED';
    case 'ACCEPTED':
      return 'IN_PROGRESS';
    case 'IN_PROGRESS':
      return 'READY';
    default:
      return null;
  }
};

const markServedMutation = useMutation({
    mutationFn: async ({ orderId, target }: { orderId: string; target: OrderStatus }) =>
      updateOrderStatus(orderId, { status: target }),
    onSuccess: () => {
      toast({
        title: t('toasts.serveSuccess.title'),
        description: t('toasts.serveSuccess.description'),
      });
      queryClient.invalidateQueries({ queryKey: ['orders', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['orders', 'recent'] });
      queryClient.invalidateQueries({ queryKey: ['kpis', 'today'] });
    },
    onError: (error: unknown) => {
      toast({
        title: t('toasts.serveError.title'),
        description: error instanceof Error ? error.message : t('toasts.serveError.description'),
        variant: 'destructive',
      });
    },
    onSettled: () => {
      setPendingOrderId(null);
    },
  });

  const cashSettlementMutation = useMutation({
    mutationFn: async ({ orderId, amount }: { orderId: string; amount: number }) =>
      payOrderCash(orderId, { amount }),
    onMutate: ({ orderId }) => {
      setCashSettlementOrderId(orderId);
    },
    onSuccess: async (response, variables) => {
      const payment = response.payment;
      const code = payment.orderCode ?? variables.orderId;
      toast({
        title: t('toasts.cashCollected.title', { defaultValue: 'Cash collected' }),
        description: t('toasts.cashCollected.descriptionWithCode', {
          defaultValue: 'Order {{code}} marked as paid.',
          code,
        }),
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['orders', 'list'] }),
        queryClient.invalidateQueries({ queryKey: ['orders', 'recent'] }),
        queryClient.invalidateQueries({ queryKey: ['payments'] }),
        queryClient.invalidateQueries({ queryKey: ['tables'] }),
      ]);
    },
    onError: (error: unknown) => {
      toast({
        title: t('toasts.cashCollected.errorTitle', { defaultValue: 'Unable to mark cash' }),
        description:
          error instanceof Error
            ? error.message
            : t('toasts.cashCollected.errorDescription', { defaultValue: 'Please try again.' }),
        variant: 'destructive',
      });
    },
    onSettled: () => {
      setCashSettlementOrderId(null);
    },
  });

  const cancelOrderMutation = useMutation({
    mutationFn: async ({ orderId }: { orderId: string; retainRefundCents?: number }) =>
      updateOrderStatus(orderId, { status: 'CANCELLED' }),
    onMutate: ({ orderId }) => {
      setCancellingOrderId(orderId);
    },
    onSuccess: async (_, variables) => {
      const cancelledOrder = cancelDialogOrder;
      const retainedRefundCents =
        typeof variables.retainRefundCents === 'number' && Number.isFinite(variables.retainRefundCents)
          ? Math.max(0, Math.round(variables.retainRefundCents))
          : 0;
      const cancelledCode = cancelledOrder?.code ?? variables.orderId;
      if (retainedRefundCents > 0) {
        toast({
          title: t('toasts.cancelOrder.keptFundsTitle', {
            defaultValue: 'Order cancelled, refund pending',
          }),
          description: t('toasts.cancelOrder.keptFundsDescription', {
            amount: formatCurrency(retainedRefundCents),
            code: cancelledCode,
            defaultValue: 'Order {{code}} still shows {{amount}} refund due until you record it.',
          }),
          variant: 'destructive',
        });
      } else {
        toast({
          title: t('toasts.cancelOrder.title', { defaultValue: 'Order cancelled' }),
          description: t('toasts.cancelOrder.description', {
            defaultValue: 'Order {{code}} moved to Cancelled.',
            code: cancelledCode,
          }),
        });
      }
      setCancelDialogOrder(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['orders', 'list'] }),
        queryClient.invalidateQueries({ queryKey: ['orders', 'recent'] }),
      ]);
    },
    onError: (error: unknown) => {
      toast({
        title: t('toasts.cancelOrder.errorTitle', { defaultValue: 'Unable to cancel order' }),
        description:
          error instanceof Error
            ? error.message
            : t('toasts.cancelOrder.errorDescription', { defaultValue: 'Please try again.' }),
        variant: 'destructive',
      });
    },
    onSettled: () => {
      setCancellingOrderId(null);
    },
  });

  const refundOrderMutation = useMutation({
    mutationFn: async ({ orderId, amount, reason }: { orderId: string; amount: number; reason?: string }) =>
      refundOrder(orderId, { amount, reason }),
    onMutate: ({ orderId }) => {
      setRefundingOrderId(orderId);
    },
    onSuccess: async (_, variables) => {
      toast({
        title: t('toasts.refundOrder.title', { defaultValue: 'Refund recorded' }),
        description: t('toasts.refundOrder.description', {
          amount: formatCurrency(variables.amount),
          code: refundDialogOrder?.code ?? variables.orderId,
          defaultValue: 'Returned {{amount}} to the guest.',
        }),
      });
      setRefundDialogOrder(null);
      setRefundReason('');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['orders', 'list'] }),
        queryClient.invalidateQueries({ queryKey: ['orders', 'recent'] }),
        queryClient.invalidateQueries({ queryKey: ['payments'] }),
      ]);
    },
    onError: (error: unknown) => {
      toast({
        title: t('toasts.refundOrder.errorTitle', { defaultValue: 'Unable to refund order' }),
        description:
          error instanceof Error
            ? error.message
            : t('toasts.refundOrder.errorDescription', { defaultValue: 'Please try again.' }),
        variant: 'destructive',
      });
    },
    onSettled: () => {
      setRefundingOrderId(null);
    },
  });

  useEffect(() => {
    if (!activeRestaurantId) {
      return;
    }

    const channel = supabase
      .channel(`orders-list-${activeRestaurantId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'orders',
        filter: `restaurant_id=eq.${activeRestaurantId}`,
      }, () => {
        console.debug('[OrdersPage] realtime update received');
        queryClient.invalidateQueries({ queryKey: ['orders', 'list'] });
        queryClient.invalidateQueries({ queryKey: ['kpis', 'today'] });
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeRestaurantId, queryClient]);

  useEffect(() => {
    if (!cancelDialogOrder) {
      setCancelPaymentAction('refund');
      return;
    }
    const refundAvailable = getRefundableCents(cancelDialogOrder);
    setCancelPaymentAction(refundAvailable > 0 ? 'refund' : 'retain');
  }, [cancelDialogOrder]);

  const handleMarkServed = (order: OrderSummary) => {
    const target = resolveOrderAdvanceTarget(order);
    if (!target || target === order.status) {
      return;
    }

    setPendingOrderId(order.id);
    markServedMutation.mutate({ orderId: order.id, target });
  };

  const handleCollectCash = (order: OrderSummary) => {
    const outstandingCents = order.totals?.totalCents ?? order.totalCents ?? 0;
    if (!outstandingCents) {
      toast({
        title: t('toasts.cashCollected.errorTitle', { defaultValue: 'Unable to mark cash' }),
        description: t('toasts.cashCollected.noAmount', {
          defaultValue: 'No outstanding cash amount is available for this order.',
        }),
        variant: 'destructive',
      });
      return;
    }
    cashSettlementMutation.mutate({ orderId: order.id, amount: Math.max(0, Math.round(outstandingCents)) });
  };

  const handleOpenCancelDialog = (order: OrderSummary) => {
    setCancelDialogOrder(order);
  };

  const handleOpenRefundDialog = (order: OrderSummary) => {
    setRefundDialogOrder(order);
    setRefundReason('');
  };

  const handleConfirmCancel = async () => {
    if (!cancelDialogOrder) {
      return;
    }
    const refundAmount = getRefundableCents(cancelDialogOrder);
    const retainedRefundCents = cancelPaymentAction === 'retain' ? refundAmount : 0;
    if (refundAmount > 0 && cancelPaymentAction === 'refund') {
      try {
        await refundOrderMutation.mutateAsync({
          orderId: cancelDialogOrder.id,
          amount: refundAmount,
        });
      } catch {
        return;
      }
    }
    cancelOrderMutation.mutate({ orderId: cancelDialogOrder.id, retainRefundCents: retainedRefundCents });
  };

  const handleConfirmRefund = () => {
    if (!refundDialogOrder) {
      return;
    }
    const amount = getRefundableCents(refundDialogOrder);
    if (amount <= 0) {
      toast({
        title: t('toasts.refundOrder.errorTitle', { defaultValue: 'Unable to refund order' }),
        description: t('toasts.refundOrder.noAmount', {
          defaultValue: 'No refundable amount is available for this order.',
        }),
        variant: 'destructive',
      });
      return;
    }
    refundOrderMutation.mutate({
      orderId: refundDialogOrder.id,
      amount,
      reason: refundReason.trim() ? refundReason.trim() : undefined,
    });
  };

  const handleDecisionConfirm = () => {
    if (!decisionDialog) {
      return;
    }
    const trimmed = decisionNote.trim();
    if (decisionDialog.decision === 'decline' && declineNoteRequired && trimmed.length === 0) {
      setDecisionNoteError('Please provide a note for decline.');
      return;
    }
    approvalDecisionMutation.mutate({
      requestId: decisionDialog.requestId,
      stepId: decisionDialog.stepId,
      decision: decisionDialog.decision,
      note: trimmed.length > 0 ? trimmed : undefined,
    });
  };

  const handleSubmitApproval = () => {
    if (!approvalRequestId) {
      return;
    }
    approvalSubmitMutation.mutate({
      requestId: approvalRequestId,
      note: submitNote.trim() ? submitNote.trim() : undefined,
    });
  };

  const handleCancelApproval = () => {
    if (!approvalRequestId) {
      return;
    }
    approvalCancelMutation.mutate({
      requestId: approvalRequestId,
      reason: cancelReason.trim() ? cancelReason.trim() : undefined,
    });
  };

  const handleEditOrder = (order: OrderSummary) => {
    if (!order) {
      return;
    }

    const isMarketplaceOrder =
      typeof order.source === 'string'
        ? ['UBER_EATS', 'DOORDASH', 'GRUBHUB'].includes(order.source.toUpperCase())
        : false;

    if (!EDITABLE_ORDER_STATUSES.has(order.status) || isMarketplaceOrder) {
      toast({
        title: t('toasts.edit.disabledTitle', { defaultValue: 'Order locked' }),
        description: t('toasts.edit.disabledDescription', {
          defaultValue:
            order.status === 'READY'
              ? 'Orders marked ready to serve cannot be edited.'
              : 'This order can no longer be modified.',
        }),
        variant: 'destructive',
      });
      return;
    }

    try {
      const itemsPayload = (order.items ?? []).map((item, index) => ({
        id: item.menuItemId ?? item.id ?? `restored-${index}`,
        name: item.name ?? `Item ${index + 1}`,
        price_cents: typeof item.priceCents === 'number' ? item.priceCents : item.totalCents ?? 0,
        price_snapshot_cents: typeof item.priceCents === 'number' ? item.priceCents : item.totalCents ?? 0,
        quantity: item.quantity ?? 1,
        notes: item.notes ?? undefined,
        category_id: 'restored',
        order_item_id: item.id ?? undefined,
        menu_item_id: item.menuItemId ?? null,
      }));

      const totalsPayload = order.totals
        ? {
            totalCents: order.totals.totalCents,
            currency: order.totals.currency,
          }
        : {
            totalCents: order.totalCents ?? 0,
            currency: order.currency ?? 'USD',
          };

      sessionStorage.setItem(
        'pos_preloaded_cart',
        JSON.stringify({
          source: 'edit',
          orderId: order.id,
          orderCode: order.code ?? null,
          tableId: order.tableId ?? null,
          totals: totalsPayload,
          paymentStatus: order.paymentStatus ?? null,
          status: order.status,
          loyaltyOptIn: typeof order.loyaltyPointsAwarded === 'number' && order.loyaltyPointsAwarded > 0,
          items: itemsPayload,
        }),
      );

      const editPath = tenantPath(`sales/new?mode=edit&orderId=${order.id}`);
      navigate(editPath);
    } catch (error) {
      console.error('[OrdersPage] failed to prepare order for edit', error);
      toast({
        title: 'Unable to edit order',
        description: 'Reload the POS page and try again.',
        variant: 'destructive',
      });
    }
  };

  const handleRefresh = () => {
    ordersQuery.refetch();
  };

  const openOrderDetails = (order: OrderSummary) => {
    setSelectedOrder(order);
    setDetailsOpen(true);
  };

  const handleDetailsOpenChange = (open: boolean) => {
    setDetailsOpen(open);
    if (!open) {
      setSelectedOrder(null);
    }
  };

  const handleOpenCounter = useCallback(() => {
    setCounterPeekKey((previous) => previous + 1);
    setCounterPeekOpen(true);
  }, []);

  const handlePopoutCounter = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.open(counterPosPath, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(counterPosPath);
  }, [counterPosPath, navigate]);

  const modalServing = selectedOrder ? markServedMutation.isPending && pendingOrderId === selectedOrder.id : false;
  const modalCanServe = selectedOrder?.status === 'READY';
  const modalCanEdit = selectedOrder
    ? EDITABLE_ORDER_STATUSES.has(selectedOrder.status) && !(
        typeof selectedOrder.source === 'string' &&
        ['UBER_EATS', 'DOORDASH', 'GRUBHUB'].includes(selectedOrder.source.toUpperCase())
      )
    : false;
  const modalCanCancel = selectedOrder
    ? !CANCEL_BLOCKED_STATUSES.has(selectedOrder.status) &&
      !(
        typeof selectedOrder.source === 'string' &&
        ['UBER_EATS', 'DOORDASH', 'GRUBHUB'].includes(selectedOrder.source.toUpperCase())
      )
    : false;
  const modalCanRefund = getRefundableCents(selectedOrder) > 0;
  const modalCancelling =
    selectedOrder && cancelOrderMutation.isPending ? cancellingOrderId === selectedOrder.id : false;
  const modalRefunding =
    selectedOrder && refundOrderMutation.isPending ? refundingOrderId === selectedOrder.id : false;
  const cancelOutstandingCents = cancelDialogOrder
    ? Math.max(
        0,
        Math.round(
          (cancelDialogOrder.outstandingBalanceCents ?? cancelDialogOrder.totals?.totalCents ?? cancelDialogOrder.totalCents ?? 0),
        ),
      )
    : 0;
  const cancelRefundCents = getRefundableCents(cancelDialogOrder);
  const cancelOutstandingLabel = formatCurrency(cancelOutstandingCents);
  const cancelRefundLabel = formatCurrency(cancelRefundCents);
  const refundDialogAmount = getRefundableCents(refundDialogOrder);
  const refundDialogLabel = formatCurrency(refundDialogAmount);
  const modalRefundDueCents = getRefundDueCents(selectedOrder);
  const modalRefundDueLabel = formatCurrency(modalRefundDueCents);
  const cancelActionPending = cancelOrderMutation.isPending || refundOrderMutation.isPending;

  const lastExportFormatRef = useRef<ExportFormat>('csv');

  const ordersExport = useExportJob({
    target: 'orders',
    getFilters: () => ({
      status: statusFilter === 'ALL' ? undefined : statusFilter,
      search: searchTerm.trim() || undefined,
      sort: 'desc',
    }),
    onSuccess: () => {
      const formatLabel = lastExportFormatRef.current.toUpperCase();
      toast({
        title: t('toasts.export.successTitle'),
        description: t('toasts.export.successDescription', { format: formatLabel }),
      });
    },
    onError: (error) => {
      toast({
        title: t('toasts.export.failureTitle', { defaultValue: 'Unable to export orders' }),
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleOrderExport = useCallback(
    (format: ExportFormat) => {
      if (orders.length === 0) {
        toast({
          title: t('toasts.export.emptyTitle'),
          description: t('toasts.export.emptyDescription'),
          variant: 'destructive',
        });
        return;
      }

      lastExportFormatRef.current = format;
      ordersExport.startExport(format);
    },
    [orders.length, ordersExport, toast, t],
  );

  return (
    <div className="mx-auto w-full max-w-[1600px] space-y-10 px-4 pb-16 pt-6 sm:px-6 lg:px-8">
      <div className="space-y-8">
        <header>
          <div className={cn(SOFT_CARD_SURFACE, 'relative overflow-hidden p-6 sm:p-7')}>
            <div className="pointer-events-none absolute inset-0 rounded-[inherit] bg-gradient-to-br from-primary/10 via-transparent to-transparent opacity-70 dark:from-primary/15" />
            <div className="relative z-10 flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
              <div className="space-y-4">
                <BackButton to="/dashboard" text={t('metadata.back')} />
                <div className="flex items-center gap-4">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/15 via-primary/5 to-transparent text-primary shadow-inner ring-1 ring-inset ring-primary/25">
                    <ShoppingBag className="h-6 w-6" />
                  </span>
                  <div>
                    <h1 className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
                      {t('metadata.title')}
                    </h1>
                    <p className="text-sm text-muted-foreground md:text-base">
                      {t('metadata.description')}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <Badge variant="outline" className={cn(SOFT_OUTLINE_BADGE, 'backdrop-blur-sm')}>
                    {lastUpdatedLabel}
                  </Badge>
                  <Badge variant="outline" className={cn(SOFT_OUTLINE_BADGE, 'backdrop-blur-sm')}>
                    {t('sections.liveQueue.activeBadge', { count: totalCount })}
                  </Badge>
                </div>
              </div>
              <div className="flex flex-col gap-3 lg:min-w-[260px]">
                <Button
                  variant="outline"
                  onClick={handleRefresh}
                  disabled={ordersQuery.isFetching}
                  className="h-12 w-full rounded-2xl bg-white/80 text-sm font-semibold text-gray-700 shadow-lg shadow-gray-500/10 ring-1 ring-inset ring-gray-200/60 transition hover:bg-white dark:bg-gray-900/40 dark:text-gray-200 dark:ring-gray-700/60 sm:w-auto"
                >
                  <RefreshCcw className={`mr-2 h-4 w-4 ${ordersQuery.isFetching ? 'animate-spin' : ''}`} />
                  {ordersQuery.isFetching ? t('actions.refresh.pending') : t('actions.refresh.label')}
                </Button>
                <Button
                  onClick={handleOpenCounter}
                  className="h-12 w-full rounded-2xl bg-gradient-to-br from-primary/90 via-primary to-primary/90 text-sm font-semibold shadow-[0_18px_36px_-22px_rgba(59,130,246,0.65)] hover:shadow-[0_20px_42px_-18px_rgba(59,130,246,0.75)] sm:w-auto"
                >
                  <Smartphone className="mr-2 h-4 w-4" />
                  {t('actions.launchCounter')}
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="h-12 w-full rounded-2xl bg-white/80 text-sm font-semibold text-gray-700 shadow-lg shadow-gray-500/10 ring-1 ring-inset ring-gray-200/60 transition hover:bg-white dark:bg-gray-900/40 dark:text-gray-200 dark:ring-gray-700/60 sm:w-auto"
                      disabled={orders.length === 0 || ordersExport.isRunning}
                    >
                      <Download className={`mr-2 h-4 w-4 ${ordersExport.isRunning ? 'animate-spin' : ''}`} />
                      {ordersExport.isRunning
                        ? t('actions.exporting', { defaultValue: 'Preparingâ€¦' })
                        : t('actions.export')}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem disabled={ordersExport.isRunning} onClick={() => handleOrderExport('csv')}>
                      CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem disabled={ordersExport.isRunning} onClick={() => handleOrderExport('json')}>
                      JSON
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        </header>

        <section className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4">
          {[{
            key: 'ordersToday',
            label: t('sections.metrics.ordersToday.label'),
            helper: t('sections.metrics.ordersToday.helper'),
            value: metrics.totalOrdersToday.toLocaleString(),
            Icon: ShoppingBag,
            accent:
              'bg-gradient-to-br from-sky-500/20 via-sky-500/5 to-transparent text-sky-600 ring-1 ring-inset ring-sky-500/25 dark:from-sky-500/25 dark:via-transparent dark:to-gray-900/40 dark:text-sky-200 dark:ring-sky-500/30',
          }, {
            key: 'revenue',
            label: t('sections.metrics.revenue.label'),
            helper: t('sections.metrics.revenue.helper'),
            value: formatCurrency(metrics.revenueToday),
            Icon: DollarSign,
            accent:
              'bg-gradient-to-br from-indigo-500/20 via-indigo-500/5 to-transparent text-indigo-600 ring-1 ring-inset ring-indigo-500/25 dark:from-indigo-500/25 dark:via-transparent dark:to-gray-900/40 dark:text-indigo-200 dark:ring-indigo-500/30',
          }, {
            key: 'active',
            label: t('sections.metrics.active.label'),
            helper: t('sections.metrics.active.helper'),
            value: metrics.activeOrders.toLocaleString(),
            Icon: Clock,
            accent:
              'bg-gradient-to-br from-blue-400/20 via-blue-400/5 to-transparent text-blue-600 ring-1 ring-inset ring-blue-400/25 dark:from-blue-500/25 dark:via-transparent dark:to-gray-900/40 dark:text-blue-200 dark:ring-blue-500/30',
          }, {
            key: 'avgOrder',
            label: t('sections.metrics.avgOrder.label'),
            helper: t('sections.metrics.avgOrder.helper'),
            value: formatCurrency(metrics.avgOrder),
            Icon: CreditCard,
            accent:
              'bg-gradient-to-br from-gray-400/25 via-gray-300/10 to-transparent text-gray-700 ring-1 ring-inset ring-gray-400/30 dark:from-gray-500/30 dark:via-transparent dark:to-gray-900/40 dark:text-gray-200 dark:ring-gray-600/35',
          }].map(({ key, label, helper, value, Icon, accent }) => (
            <Card
              key={key}
              className={cn(
                SOFT_CARD_SURFACE,
                'transition-all duration-300 hover:shadow-[0_20px_45px_-30px_rgba(15,23,42,0.55)] dark:hover:border-primary/20',
              )}
            >
              <CardContent className="space-y-5 p-6">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
                    <p className="mt-2 text-3xl font-semibold text-foreground">{value}</p>
                  </div>
                  <span
                    className={cn(
                      'flex h-10 w-10 items-center justify-center rounded-xl p-2 shadow-inner ring-1 ring-inset ring-black/5 dark:ring-white/5',
                      accent,
                    )}
                  >
                    <Icon className="h-5 w-5" />
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">{helper}</p>
              </CardContent>
            </Card>
          ))}
        </section>
      </div>

      <div className="space-y-6">
        <Card className={cn(SOFT_CARD_SURFACE, 'backdrop-blur-md')}>
          <CardHeader className={cn(SOFT_SECTION_HEADER, 'pb-4')}>
            <CardTitle className="flex items-center gap-2 text-base">
              <Filter className="h-5 w-5 text-muted-foreground" />
              {t('sections.controls.title')}
            </CardTitle>
            <CardDescription>{t('sections.controls.description')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 pt-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="relative w-full min-w-0 flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder={t('sections.controls.searchPlaceholder')}
                  aria-label={t('sections.controls.searchAria', { defaultValue: 'Search orders' })}
                  className="h-11 rounded-2xl border-transparent bg-white/80 pl-10 text-sm text-gray-700 shadow-sm ring-1 ring-inset ring-gray-200/60 placeholder:text-gray-400 focus-visible:ring-2 focus-visible:ring-primary/30 dark:bg-gray-900/40 dark:text-gray-200 dark:ring-gray-700/60 dark:placeholder:text-gray-500"
                />
              </div>
              <OrdersViewToggle
                value={viewMode}
                onChange={handleViewModeChange}
                disabled={ordersQuery.isFetching}
                className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row"
                labels={{
                  table: t('sections.controls.viewModes.table'),
                  cards: t('sections.controls.viewModes.cards'),
                }}
              />
            </div>
            {/* Mobile status selector */}
            <div className="sm:hidden">
              <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as OrderStatus | 'ALL')}>
                <SelectTrigger
                  aria-label={t('sections.controls.statusAria', { defaultValue: 'Filter by status' })}
                  className="h-10 w-full rounded-xl bg-white/80 text-sm font-medium text-gray-700 ring-1 ring-inset ring-gray-200/60 hover:bg-white dark:bg-gray-900/40 dark:text-gray-200 dark:ring-gray-700/60"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((status) => (
                    <SelectItem key={status} value={status}>
                      {status === 'ALL' ? t('status.options.ALL') : t(['status.options', status].join('.'))}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Tabs
              value={statusFilter}
              onValueChange={(value) => setStatusFilter(value as OrderStatus | 'ALL')}
              className="w-full hidden sm:block"
            >
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-white to-transparent dark:from-gray-900" />
                <div className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-white to-transparent dark:from-gray-900" />
                <TabsList className="flex w-full items-center gap-2 overflow-x-auto whitespace-nowrap rounded-xl bg-transparent p-1 pl-6 pr-6 [scrollbar-width:none] [-ms-overflow-style:'none'] [&::-webkit-scrollbar]:hidden">
                  {STATUS_OPTIONS.map((status) => (
                    <TabsTrigger
                      key={status}
                      value={status}
                      className="rounded-full bg-white/80 px-3 py-1.5 text-xs font-medium uppercase tracking-wide text-gray-600 shadow-sm ring-1 ring-inset ring-gray-200/60 transition-colors backdrop-blur-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:ring-primary/30 dark:bg-gray-900/40 dark:text-gray-300 dark:ring-gray-700/60 dark:data-[state=active]:bg-primary/20 dark:data-[state=active]:text-primary dark:data-[state=active]:ring-primary/40"
                    >
                      {status === 'ALL'
                        ? t('status.options.ALL')
                        : t(['status.options', status].join('.'))}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>
            </Tabs>
            <p className="text-center text-xs font-medium text-muted-foreground sm:text-left">
              {summaryText}
            </p>
          </CardContent>
        </Card>
        <Card className={cn(SOFT_CARD_SURFACE, 'backdrop-blur-md')}>
          <CardHeader
            className={cn(
              'flex flex-col gap-3 pb-4 sm:flex-row sm:items-center sm:justify-between',
              SOFT_SECTION_HEADER,
            )}
          >
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <ShoppingBag className="h-5 w-5 text-muted-foreground" />
                {t('sections.liveQueue.title')}
              </CardTitle>
              <CardDescription>{t('sections.liveQueue.description')}</CardDescription>
            </div>
            <Badge variant="outline" className={cn(SOFT_OUTLINE_BADGE, 'text-xs font-medium backdrop-blur-sm')}>
              {t('sections.liveQueue.activeBadge', { count: totalCount })}
            </Badge>
          </CardHeader>
          <CardContent className="space-y-6">
            {ordersQuery.isError ? (
              <Alert
                variant="destructive"
                className="flex flex-col gap-3 rounded-2xl bg-rose-500/10 p-4 text-rose-700 ring-1 ring-inset ring-rose-500/25 backdrop-blur-sm sm:flex-row sm:items-center sm:justify-between dark:bg-rose-500/15 dark:text-rose-200 dark:ring-rose-500/30"
              >
                <div className="flex items-start gap-3">
                  <span className="mt-1 flex h-8 w-8 items-center justify-center rounded-xl bg-rose-500/20 text-rose-600 ring-1 ring-inset ring-rose-500/40 dark:bg-rose-500/25 dark:text-rose-100">
                    <AlertTriangle className="h-4 w-4" />
                  </span>
                  <div>
                    <AlertTitle className="text-sm font-semibold">
                      {t('sections.liveQueue.error.title')}
                    </AlertTitle>
                    <AlertDescription className="text-xs text-muted-foreground">
                      {t('sections.liveQueue.error.description')}
                    </AlertDescription>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full rounded-xl bg-white/80 text-xs font-semibold text-rose-700 shadow-sm ring-1 ring-inset ring-rose-200/50 hover:bg-white dark:bg-gray-900/30 dark:text-rose-200 dark:ring-rose-500/30 sm:w-auto"
                  onClick={handleRefresh}
                >
                  <RefreshCcw className="mr-2 h-4 w-4" />
                  {ordersQuery.isFetching ? t('actions.refresh.pending') : t('actions.refresh.label')}
                </Button>
              </Alert>
            ) : null}
            {ordersQuery.isLoading && orders.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 py-12 text-sm text-muted-foreground">
                <RefreshCcw className="h-5 w-5 animate-spin" />
                {t('sections.liveQueue.loading')}
              </div>
            ) : orders.length === 0 ? (
              <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-gray-200/50 bg-white/70 py-12 text-center text-sm text-gray-600 backdrop-blur-sm dark:border-gray-700/60 dark:bg-gray-900/30 dark:text-gray-300">
                {t('sections.liveQueue.empty')}
              </div>
            ) : viewMode === 'table' ? (
              <div className={cn(SOFT_TABLE_SURFACE, 'overflow-hidden')}>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader className="border-b border-gray-200/60 bg-white/80 text-gray-600 dark:border-gray-700/60 dark:bg-gray-900/40 dark:text-gray-300">
                      <TableRow>
                        <TableHead className="w-[160px] text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          {t('tables.main.headers.order')}
                        </TableHead>
                        <TableHead className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          {t('tables.main.headers.table')}
                        </TableHead>
                        <TableHead className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          {t('tables.main.headers.items')}
                        </TableHead>
                        <TableHead className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          {t('tables.main.headers.status')}
                        </TableHead>
                        <TableHead className="min-w-[170px] text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          {t('tables.main.headers.payment', { defaultValue: 'Payment' })}
                        </TableHead>
                        <TableHead className="w-[120px] text-right text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          {t('tables.main.headers.total')}
                        </TableHead>
                        <TableHead className="w-[110px] text-right text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          {t('tables.main.headers.loyalty', { defaultValue: 'Loyalty' })}
                        </TableHead>
                        <TableHead className="w-[240px] text-right text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          {t('tables.main.headers.actions')}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {orders.map((order) => {
                        const isServing = markServedMutation.isPending && pendingOrderId === order.id;
                        const advanceTarget = resolveOrderAdvanceTarget(order);
                        const canMarkServed = Boolean(advanceTarget);
                        const isMarketplaceOrder = typeof order.source === 'string'
                          ? ['UBER_EATS', 'DOORDASH', 'GRUBHUB'].includes(order.source.toUpperCase())
                          : false;
                        const canEdit = EDITABLE_ORDER_STATUSES.has(order.status) && !isMarketplaceOrder;
                        const paymentStatusNormalized = typeof order.paymentStatus === 'string'
                          ? order.paymentStatus.toUpperCase()
                          : 'UNPAID';
                        const paymentStatusLabel = PAYMENT_STATUS_LABELS[paymentStatusNormalized] ?? paymentStatusNormalized;
                        const paymentMethodRaw = typeof order.payment?.method === 'string'
                          ? order.payment.method
                          : typeof order.paymentMethod === 'string'
                            ? order.paymentMethod
                            : '';
                        const paymentMethodNormalized = paymentMethodRaw.toLowerCase();
                        const paymentMethodLabel = paymentMethodNormalized
                          ? paymentMethodNormalized.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
                          : t('tables.main.payment.unknown', { defaultValue: 'Unknown method' });
                        const isCashPayment = paymentMethodNormalized === 'cash';
                        const isCashPending = isCashPayment && !['PAID', 'REFUNDED', 'VOIDED'].includes(paymentStatusNormalized);
                        const outstandingCents = Math.max(
                          0,
                          Math.round(order.outstandingBalanceCents ?? order.totals?.totalCents ?? order.totalCents ?? 0),
                        );
                        const outstandingLabel = formatCurrency(outstandingCents);
                        const isSettlingCash = cashSettlementMutation.isPending && cashSettlementOrderId === order.id;
                        const refundAvailableCents = getRefundableCents(order);
                        const refundLabel = formatCurrency(refundAvailableCents);
                        const refundDueCents = getRefundDueCents(order);
                        const refundDueLabel = formatCurrency(refundDueCents);
                        const hasRefundDue = refundDueCents > 0;
                        const canCancel = !CANCEL_BLOCKED_STATUSES.has(order.status) && !isMarketplaceOrder;
                        const canRefund = refundAvailableCents > 0;
                        const isCancelling = cancelOrderMutation.isPending && cancellingOrderId === order.id;
                        const isRefunding = refundOrderMutation.isPending && refundingOrderId === order.id;

                        return (
                          <TableRow key={order.id} className="transition-colors hover:bg-muted/15">
                            <TableCell className="align-top">
                              <div className="font-semibold text-foreground">{order.code || order.id}</div>
                              <div className="mt-1 text-xs text-muted-foreground">{formatRelativeTime(order.placedAt)}</div>
                            </TableCell>
                            <TableCell className="align-top">
                              <div className="text-sm font-medium text-foreground">{order.table || t('tables.main.counterFallback')}</div>
                              {order.customerName && (
                                <div className="mt-1 text-xs text-muted-foreground">{order.customerName}</div>
                              )}
                            </TableCell>
                            <TableCell className="align-top">
                              <div className="space-y-1 text-sm text-muted-foreground">
                                {order.items?.slice(0, 3).map((item, index) => (
                                  <div key={index} className="flex justify-between">
                                    <span>{item.quantity}x {item.name}</span>
                                  </div>
                                ))}
                                {(order.items?.length ?? 0) > 3 && (
                                  <div className="text-xs">
                                    {t('tables.main.moreItems', { count: (order.items?.length ?? 0) - 3 })}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="align-top">
                              <Badge className={cn('text-[11px] font-medium', STATUS_BADGE_STYLES[order.status] ?? SOFT_OUTLINE_BADGE)}>
                                {t(['status.options', order.status].join('.'))}
                              </Badge>
                            </TableCell>
                            <TableCell className="align-top">
                              <div className="flex flex-col gap-1.5 text-left">
                                <div className="flex items-center justify-between gap-2">
                                  <span className="truncate text-sm font-medium text-foreground">{paymentMethodLabel}</span>
                                  <Badge className={cn('whitespace-nowrap px-2 py-0 text-[10px] uppercase tracking-wide', PAYMENT_BADGE_STYLES[paymentStatusNormalized] ?? SOFT_OUTLINE_BADGE)}>
                                    {paymentStatusLabel}
                                  </Badge>
                                </div>
                                {isCashPending ? (
                                  <div className="flex flex-col gap-1 text-xs text-muted-foreground">
                                    <div className="flex items-center justify-between">
                                      <span>{outstandingLabel}</span>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-8 rounded-lg border-amber-500/25 bg-amber-500/8 px-3 text-[11px] font-semibold text-amber-700 hover:bg-amber-500/12 dark:border-amber-400/30 dark:bg-amber-400/12 dark:text-amber-100"
                                        onClick={() => handleCollectCash(order)}
                                        disabled={isSettlingCash}
                                      >
                                        {isSettlingCash ? (
                                          <>
                                            <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                                            {t('actions.collectCash.processing', { defaultValue: 'Marking...' })}
                                          </>
                                        ) : (
                                          <>
                                            <Banknote className="mr-2 h-3.5 w-3.5" />
                                            {t('actions.collectCash.label', { defaultValue: 'Collect cash' })}
                                          </>
                                        )}
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-xs text-muted-foreground">{paymentStatusLabel}</span>
                                )}
                                {hasRefundDue ? (
                                  <Badge
                                    variant="destructive"
                                    size="xs"
                                    className="mt-1 w-max gap-1 rounded-lg bg-destructive/15 text-[10px] font-semibold text-destructive dark:bg-destructive/20"
                                  >
                                    <AlertTriangle className="h-3 w-3" />
                                    {t('tables.main.payment.refundDue', { defaultValue: 'Refund due' })}
                                    <span className="font-normal text-destructive/80 dark:text-destructive/70">
                                      {' - '}
                                      {refundDueLabel}
                                    </span>
                                  </Badge>
                                ) : null}
                              </div>
                            </TableCell>
                            <TableCell className="align-top text-right font-semibold text-foreground">
                              {formatCurrency(order.totalCents)}
                            </TableCell>
                            <TableCell className="align-top text-right">
                              {order.loyaltyPointsAwarded && order.loyaltyPointsAwarded > 0 ? (
                                <span className="font-semibold text-emerald-600 dark:text-emerald-200">
                                  {order.loyaltyPointsAwarded.toLocaleString()}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell className="align-top text-right">
                              <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:flex-wrap sm:justify-end sm:gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="rounded-xl bg-white/80 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-200/60 hover:bg-white dark:bg-gray-900/40 dark:text-gray-200 dark:ring-gray-700/60 sm:w-auto"
                                  onClick={() => openOrderDetails(order)}
                                >
                                  {t('actions.view')}
                                </Button>
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  className="rounded-xl text-sm font-medium sm:w-auto"
                                  disabled={!canEdit}
                                  onClick={() => handleEditOrder(order)}
                                >
                                  {t('actions.edit', { defaultValue: 'Edit' })}
                                </Button>
                                <Button
                                  size="sm"
                                  className="rounded-xl text-sm font-medium sm:w-auto"
                                  disabled={!canMarkServed || isServing}
                                  onClick={() => handleMarkServed(order)}
                                >
                                  <CheckCircle className="mr-2 h-4 w-4" />
                                  {isServing ? t('actions.markServed.pending') : t('actions.markServed.label')}
                                </Button>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="rounded-xl bg-white/80 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-200/60 hover:bg-white dark:bg-gray-900/40 dark:text-gray-200 dark:ring-gray-700/60 sm:w-auto"
                                      aria-label={t('actions.more', { defaultValue: 'More actions' })}
                                    >
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-60">
                                    <DropdownMenuItem
                                      disabled={!canCancel || isCancelling}
                                      onSelect={(event) => {
                                        event.preventDefault();
                                        if (canCancel && !isCancelling) {
                                          handleOpenCancelDialog(order);
                                        }
                                      }}
                                      className="flex items-start gap-3"
                                    >
                                      {isCancelling ? (
                                        <Loader2 className="mt-0.5 h-4 w-4 animate-spin text-muted-foreground" />
                                      ) : (
                                        <Ban className="mt-0.5 h-4 w-4 text-destructive" />
                                      )}
                                      <div className="flex flex-col">
                                        <span className="text-sm font-medium">
                                          {isCancelling
                                            ? t('actions.cancel.pending', { defaultValue: 'Cancelling...' })
                                            : t('actions.cancel.label', { defaultValue: 'Cancel order' })}
                                        </span>
                                        <span className="text-xs text-muted-foreground">
                                          {t('modals.cancel.paymentDescription', {
                                            amount: refundLabel,
                                            defaultValue: 'Handle refunds before closing the ticket.',
                                          })}
                                        </span>
                                      </div>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      disabled={!canRefund || isRefunding}
                                      onSelect={(event) => {
                                        event.preventDefault();
                                        if (canRefund && !isRefunding) {
                                          handleOpenRefundDialog(order);
                                        }
                                      }}
                                      className="flex items-start gap-3"
                                    >
                                      {isRefunding ? (
                                        <Loader2 className="mt-0.5 h-4 w-4 animate-spin text-muted-foreground" />
                                      ) : (
                                        <RotateCcw className="mt-0.5 h-4 w-4 text-sky-500" />
                                      )}
                                      <div className="flex flex-col">
                                        <span className="text-sm font-medium">
                                          {isRefunding
                                            ? t('actions.refund.pending', { defaultValue: 'Processing...' })
                                            : t('actions.refund.label', { defaultValue: 'Refund guest' })}
                                        </span>
                                        <span className="text-xs text-muted-foreground">{refundLabel}</span>
                                      </div>
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {orders.map((order) => {
                  const isServing = markServedMutation.isPending && pendingOrderId === order.id;
                  const advanceTarget = resolveOrderAdvanceTarget(order);
                  const canMarkServed = Boolean(advanceTarget);
                  const isMarketplaceOrder = typeof order.source === 'string'
                    ? ['UBER_EATS', 'DOORDASH', 'GRUBHUB'].includes(order.source.toUpperCase())
                    : false;
                  const canEdit = EDITABLE_ORDER_STATUSES.has(order.status) && !isMarketplaceOrder;
                  const items = order.items ?? [];
                  const paymentStatusNormalized = typeof order.paymentStatus === 'string'
                    ? order.paymentStatus.toUpperCase()
                    : 'UNPAID';
                  const paymentStatusLabel = PAYMENT_STATUS_LABELS[paymentStatusNormalized] ?? paymentStatusNormalized;
                  const paymentMethodRaw = typeof order.payment?.method === 'string'
                    ? order.payment.method
                    : typeof order.paymentMethod === 'string'
                      ? order.paymentMethod
                      : '';
                  const paymentMethodNormalized = paymentMethodRaw.toLowerCase();
                  const paymentMethodLabel = paymentMethodNormalized
                    ? paymentMethodNormalized.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
                    : t('tables.main.payment.unknown', { defaultValue: 'Unknown method' });
                  const isCashPayment = paymentMethodNormalized === 'cash';
                  const isCashPending = isCashPayment && !['PAID', 'REFUNDED', 'VOIDED'].includes(paymentStatusNormalized);
                  const outstandingCents = Math.max(
                    0,
                    Math.round(order.outstandingBalanceCents ?? order.totals?.totalCents ?? order.totalCents ?? 0),
                  );
                  const outstandingLabel = formatCurrency(outstandingCents);
                  const isSettlingCash = cashSettlementMutation.isPending && cashSettlementOrderId === order.id;
                  const refundAvailableCents = getRefundableCents(order);
                  const refundLabel = formatCurrency(refundAvailableCents);
                  const refundDueCents = getRefundDueCents(order);
                  const refundDueLabel = formatCurrency(refundDueCents);
                  const hasRefundDue = refundDueCents > 0;
                  const canCancel = !CANCEL_BLOCKED_STATUSES.has(order.status) && !isMarketplaceOrder;
                  const canRefund = refundAvailableCents > 0;
                  const isCancelling = cancelOrderMutation.isPending && cancellingOrderId === order.id;
                  const isRefunding = refundOrderMutation.isPending && refundingOrderId === order.id;

                  return (
                    <Card
                      key={order.id}
                      className={cn(
                        SOFT_CARD_SURFACE,
                        'flex h-full flex-col justify-between transition-all duration-300 hover:shadow-[0_20px_45px_-30px_rgba(15,23,42,0.55)]',
                        isCashPending ? 'ring-1 ring-amber-500/35 ring-offset-2 ring-offset-transparent' : null,
                      )}
                    >
                      <CardHeader className="space-y-2 pb-4">
                        <div className="flex items-start justify-between gap-3">
                          <CardTitle className="text-lg font-semibold text-foreground">{order.code || order.id}</CardTitle>
                          <Badge className={cn('text-[11px] font-medium', STATUS_BADGE_STYLES[order.status] ?? SOFT_OUTLINE_BADGE)}>
                            {t(['status.options', order.status].join('.'))}
                          </Badge>
                        </div>
                        <CardDescription className="text-xs uppercase tracking-wide text-muted-foreground">
                          {t('sections.cards.placed', { time: formatRelativeTime(order.placedAt) })}
                        </CardDescription>
                        <div className="flex flex-wrap items-center gap-2 text-xs">
                          <Badge className={cn('text-[11px] font-medium', PAYMENT_BADGE_STYLES[paymentStatusNormalized] ?? SOFT_OUTLINE_BADGE)}>
                            {paymentMethodNormalized ? `${paymentMethodLabel} â€¢ ${paymentStatusLabel}` : paymentStatusLabel}
                          </Badge>
                          {isCashPending && (
                            <span className="font-semibold text-amber-700 dark:text-amber-200">{outstandingLabel}</span>
                          )}
                          {hasRefundDue ? (
                            <Badge
                              variant="destructive"
                              size="xs"
                              className="gap-1 rounded-lg bg-destructive/15 text-[10px] font-semibold text-destructive dark:bg-destructive/20"
                            >
                              <AlertTriangle className="h-3 w-3" />
                              {t('tables.main.payment.refundDue', { defaultValue: 'Refund due' })}
                              <span className="font-normal text-destructive/80 dark:text-destructive/70">
                                {' - '}
                                {refundDueLabel}
                              </span>
                            </Badge>
                          ) : null}
                        </div>
                      </CardHeader>
                      <CardContent className="flex flex-1 flex-col justify-between space-y-4">
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-foreground">{order.table || t('sections.cards.counterFallback')}</span>
                            <span className="text-muted-foreground">{formatCurrency(order.totalCents)}</span>
                          </div>
                          {order.customerName && (
                            <div className="text-muted-foreground">
                              {t('sections.cards.customer', { name: order.customerName })}
                            </div>
                          )}
                        </div>
                        <div className="space-y-1 rounded-2xl bg-white/80 p-3 text-sm text-muted-foreground ring-1 ring-inset ring-gray-200/50 backdrop-blur-sm dark:bg-gray-900/40 dark:ring-gray-700/60">
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            {t('sections.cards.itemsLabel')}
                          </p>
                          {items.slice(0, 4).map((item, index) => (
                            <div key={index} className="flex justify-between">
                              <span>{item.quantity}x {item.name}</span>
                            </div>
                          ))}
                          {items.length > 4 && (
                            <div className="text-xs">
                              {t('sections.cards.additionalItems', { count: items.length - 4 })}
                            </div>
                          )}
                          {items.length === 0 && (
                            <div className="text-xs text-muted-foreground/70">
                              {t('sections.cards.noItems')}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:justify-end">
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full rounded-xl bg-white/80 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-200/60 hover:bg-white dark:bg-gray-900/40 dark:text-gray-200 dark:ring-gray-700/60 sm:w-auto"
                            onClick={() => openOrderDetails(order)}
                          >
                            {t('actions.viewDetails')}
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            className="w-full rounded-xl text-sm font-medium sm:w-auto"
                            disabled={!canEdit}
                            onClick={() => handleEditOrder(order)}
                          >
                            {t('actions.edit', { defaultValue: 'Edit' })}
                          </Button>
                          {isCashPending && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="w-full rounded-xl border-amber-500/25 bg-amber-500/10 text-sm font-medium text-amber-700 hover:bg-amber-500/15 dark:border-amber-400/30 dark:bg-amber-400/15 dark:text-amber-100 sm:w-auto"
                              onClick={() => handleCollectCash(order)}
                              disabled={isSettlingCash}
                            >
                              {isSettlingCash ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  {t('actions.collectCash.processing', { defaultValue: 'Marking...' })}
                                </>
                              ) : (
                                <>
                                  <Banknote className="mr-2 h-4 w-4" />
                                  {t('actions.collectCash.label', { defaultValue: 'Collect cash' })}
                                </>
                              )}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            className="w-full rounded-xl text-sm font-medium sm:w-auto"
                            disabled={!canMarkServed || isServing}
                            onClick={() => handleMarkServed(order)}
                          >
                            {isServing ? t('actions.markServed.pending') : t('actions.markServed.label')}
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full rounded-xl bg-white/80 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-200/60 hover:bg-white dark:bg-gray-900/40 dark:text-gray-200 dark:ring-gray-700/60 sm:w-auto"
                                aria-label={t('actions.more', { defaultValue: 'More actions' })}
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-60">
                              <DropdownMenuItem
                                disabled={!canCancel || isCancelling}
                                onSelect={(event) => {
                                  event.preventDefault();
                                  if (canCancel && !isCancelling) {
                                    handleOpenCancelDialog(order);
                                  }
                                }}
                                className="flex items-start gap-3"
                              >
                                {isCancelling ? (
                                  <Loader2 className="mt-0.5 h-4 w-4 animate-spin text-muted-foreground" />
                                ) : (
                                  <Ban className="mt-0.5 h-4 w-4 text-destructive" />
                                )}
                                <div className="flex flex-col">
                                  <span className="text-sm font-medium">
                                    {isCancelling
                                      ? t('actions.cancel.pending', { defaultValue: 'Cancelling...' })
                                      : t('actions.cancel.label', { defaultValue: 'Cancel order' })}
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    {t('modals.cancel.paymentDescription', {
                                      amount: refundLabel,
                                      defaultValue: 'Handle refunds before closing the ticket.',
                                    })}
                                  </span>
                                </div>
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                disabled={!canRefund || isRefunding}
                                onSelect={(event) => {
                                  event.preventDefault();
                                  if (canRefund && !isRefunding) {
                                    handleOpenRefundDialog(order);
                                  }
                                }}
                                className="flex items-start gap-3"
                              >
                                {isRefunding ? (
                                  <Loader2 className="mt-0.5 h-4 w-4 animate-spin text-muted-foreground" />
                                ) : (
                                  <RotateCcw className="mt-0.5 h-4 w-4 text-sky-500" />
                                )}
                                <div className="flex flex-col">
                                  <span className="text-sm font-medium">
                                    {isRefunding
                                      ? t('actions.refund.pending', { defaultValue: 'Processing...' })
                                      : t('actions.refund.label', { defaultValue: 'Refund guest' })}
                                  </span>
                                  <span className="text-xs text-muted-foreground">{refundLabel}</span>
                                </div>
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
            {orders.length > 0 && !ordersQuery.isError ? (
              <div className="flex flex-col gap-3 border-t border-gray-200/60 pt-4 sm:flex-row sm:items-center sm:justify-between dark:border-gray-700/60">
                <div className="text-xs font-medium text-muted-foreground">
                  {t('sections.liveQueue.pagination.pageStatus', {
                    page: currentPage,
                    pages: totalPages,
                  })}
                </div>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {t('sections.liveQueue.pagination.rowsLabel')}
                    </span>
                    <Select
                      value={currentPageSize.toString()}
                      onValueChange={(value) => setPageSize(Number(value))}
                    >
                      <SelectTrigger className="h-9 w-[100px] rounded-xl bg-white/80 text-xs font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-200/60 hover:bg-white dark:bg-gray-900/40 dark:text-gray-200 dark:ring-gray-700/60">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PAGE_SIZE_OPTIONS.map((option) => (
                          <SelectItem key={option} value={option.toString()}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {totalPages > 1 ? (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-xl bg-white/80 text-xs font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-200/60 hover:bg-white dark:bg-gray-900/40 dark:text-gray-200 dark:ring-gray-700/60"
                        disabled={!hasPreviousPage || ordersQuery.isFetching}
                        onClick={() => setPageIndex((prev) => Math.max(prev - 1, 0))}
                      >
                        <ChevronLeft className="mr-1 h-4 w-4" />
                        {t('sections.liveQueue.pagination.previous')}
                      </Button>
                      <span className="text-xs text-muted-foreground">
                        {currentPage} / {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-xl bg-white/80 text-xs font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-200/60 hover:bg-white dark:bg-gray-900/40 dark:text-gray-200 dark:ring-gray-700/60"
                        disabled={!hasNextPage || ordersQuery.isFetching}
                        onClick={() => setPageIndex((prev) => Math.min(prev + 1, totalPages - 1))}
                      >
                        {t('sections.liveQueue.pagination.next')}
                        <ChevronRight className="ml-1 h-4 w-4" />
                      </Button>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>

        <Sheet open={detailsOpen && Boolean(selectedOrder)} onOpenChange={handleDetailsOpenChange}>
          <SheetContent side="right" className="w-full max-w-3xl bg-background p-0">
            <SheetHeader className="px-6 pt-6 pb-4 text-left">
              <SheetTitle>
                {t('modals.details.title', { code: selectedOrder?.code || selectedOrder?.id })}
              </SheetTitle>
            </SheetHeader>
            {selectedOrder ? (
              <div className="space-y-5 px-6 pb-6">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Badge className={cn('text-[11px] font-medium', STATUS_BADGE_STYLES[selectedOrder.status] ?? SOFT_OUTLINE_BADGE)}>
                    {t(['status.options', selectedOrder.status].join('.'))}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    {t('modals.details.placed', { time: formatRelativeTime(selectedOrder.placedAt) })}
                  </span>
                </div>
                <div className="grid gap-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">{t('modals.details.tableLabel')}</span>
                    <span className="font-medium">{selectedOrder.table || t('modals.details.counterFallback')}</span>
                  </div>
                  {selectedOrder.customerName && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">{t('modals.details.customerLabel')}</span>
                      <span className="font-medium">{selectedOrder.customerName}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">{t('modals.details.totalLabel')}</span>
                    <span className="font-semibold">{formatCurrency(selectedOrder.totalCents)}</span>
                  </div>
                  {modalRefundDueCents > 0 ? (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">
                        {t('tables.main.payment.refundDue', { defaultValue: 'Refund due' })}
                      </span>
                      <Badge
                        variant="destructive"
                        size="sm"
                        className="gap-1 rounded-lg bg-destructive/10 text-destructive"
                      >
                        <AlertTriangle className="h-3.5 w-3.5" />
                        {modalRefundDueLabel}
                      </Badge>
                    </div>
                  ) : null}
                  {selectedOrder.loyaltyPointsAwarded ? (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">
                        {t('modals.details.loyaltyLabel', { defaultValue: 'Loyalty points awarded' })}
                      </span>
                      <span className="font-semibold text-emerald-600 dark:text-emerald-300">
                        {selectedOrder.loyaltyPointsAwarded}
                      </span>
                    </div>
                  ) : null}
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground">
                    {t('modals.details.itemsLabel')}
                  </h4>
                  {selectedOrder.items && selectedOrder.items.length > 0 ? (
                    <div className="mt-3 space-y-3">
                      {selectedOrder.items.map((item, index) => {
                        const modifierGroups = Array.isArray(item.modifiers) ? item.modifiers : [];
                        const variantGroups = modifierGroups.filter((group) => group.kind === 'variant');
                        const addOnGroups = modifierGroups.filter((group) => group.kind !== 'variant');
                        const allergens = Array.isArray(item.allergens)
                          ? item.allergens.filter((tag) => typeof tag === 'string' && tag.trim().length > 0)
                          : [];
                        const unitPriceCents = item?.pricing?.unitPriceCents ?? item.priceCents ?? 0;
                        const lineTotalCents =
                          item?.totalCents ?? unitPriceCents * (typeof item.quantity === 'number' ? item.quantity : 1);

                        return (
                          <div
                            key={item.id ?? `${item.name}-${index}`}
                            className="rounded-xl bg-white/80 p-4 text-sm ring-1 ring-inset ring-gray-200/50 backdrop-blur-sm dark:bg-gray-900/40 dark:ring-gray-700/60"
                          >
                            <div className="flex flex-wrap items-start justify-between gap-3">
                              <div className="space-y-1">
                                <p className="text-sm font-semibold text-foreground">
                                  {item.quantity}Ã- {item.name}
                                </p>
                                {item.status ? (
                                  <span className="text-[11px] uppercase tracking-wide text-muted-foreground/80">
                                    {item.status}
                                  </span>
                                ) : null}
                                {item.notes ? (
                                  <p className="text-xs text-muted-foreground">{item.notes}</p>
                                ) : null}
                              </div>
                              <div className="text-right">
                                <div className="text-sm font-semibold text-foreground">
                                  {formatCurrency(lineTotalCents)}
                                </div>
                                {item.quantity > 1 ? (
                                  <div className="text-xs text-muted-foreground">
                                    {formatCurrency(unitPriceCents)} ea
                                  </div>
                                ) : null}
                              </div>
                            </div>

                            {variantGroups.length > 0 ? (
                              <div className="mt-3 space-y-2">
                                <p className="text-xs font-semibold uppercase text-muted-foreground">Variants</p>
                                {variantGroups.map((group) => (
                                  <div key={group.id} className="space-y-1">
                                    <p className="text-xs font-medium text-muted-foreground/80">{group.name}</p>
                                    <div className="flex flex-wrap gap-2">
                                      {group.selections.map((selection) => {
                                        const selectionLabel =
                                          selection.quantity > 1
                                            ? `${selection.quantity}Ã- ${selection.name}`
                                            : selection.name;
                                        const deltaLabel =
                                          selection.priceOverrideCents !== null
                                            ? formatCurrency(selection.priceOverrideCents)
                                            : selection.priceDeltaCents !== 0
                                              ? `${selection.priceDeltaCents > 0 ? '+' : ''}${formatCurrency(selection.priceDeltaCents)}`
                                              : null;
                                        return (
                                          <span
                                            key={`${group.id}-${selection.id}`}
                                            className="inline-flex items-center gap-1 rounded-full bg-white/80 px-2 py-1 text-xs text-foreground ring-1 ring-inset ring-gray-200/60 backdrop-blur-sm dark:bg-gray-900/40 dark:text-gray-100 dark:ring-gray-700/60"
                                          >
                                            <span className="font-medium">{selectionLabel}</span>
                                            {deltaLabel ? (
                                              <span className="text-[10px] text-muted-foreground">{deltaLabel}</span>
                                            ) : null}
                                            {selection.allergenTags.map((tag) => (
                                              <span
                                                key={tag}
                                                className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-600"
                                              >
                                                <AlertTriangle className="h-3 w-3" />
                                                {tag}
                                              </span>
                                            ))}
                                          </span>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : null}

                            {addOnGroups.length > 0 ? (
                              <div className="mt-3 space-y-2">
                                <p className="text-xs font-semibold uppercase text-muted-foreground">
                                  {t('modals.details.modifiersLabel', { defaultValue: 'Add-ons' })}
                                </p>
                                {addOnGroups.map((group) => (
                                  <div key={group.id} className="space-y-1">
                                    <p className="text-xs font-medium text-muted-foreground/80">{group.name}</p>
                                    <div className="flex flex-wrap gap-2">
                                      {group.selections.map((selection) => {
                                        const selectionLabel =
                                          selection.quantity > 1
                                            ? `${selection.quantity}Ã- ${selection.name}`
                                            : selection.name;
                                        const deltaLabel =
                                          selection.priceOverrideCents !== null
                                            ? formatCurrency(selection.priceOverrideCents)
                                            : selection.priceDeltaCents !== 0
                                              ? `${selection.priceDeltaCents > 0 ? '+' : ''}${formatCurrency(selection.priceDeltaCents)}`
                                              : null;
                                        return (
                                          <span
                                            key={`${group.id}-${selection.id}`}
                                            className="inline-flex items-center gap-1 rounded-full bg-white/80 px-2 py-1 text-xs text-foreground ring-1 ring-inset ring-gray-200/60 backdrop-blur-sm dark:bg-gray-900/40 dark:text-gray-100 dark:ring-gray-700/60"
                                          >
                                            <span className="font-medium">{selectionLabel}</span>
                                            {deltaLabel ? (
                                              <span className="text-[10px] text-muted-foreground">{deltaLabel}</span>
                                            ) : null}
                                            {selection.allergenTags.map((tag) => (
                                              <span
                                                key={tag}
                                                className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-600"
                                              >
                                                <AlertTriangle className="h-3 w-3" />
                                                {tag}
                                              </span>
                                            ))}
                                          </span>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : null}

                            {allergens.length > 0 ? (
                              <div className="mt-3 space-y-1">
                                <p className="text-xs font-semibold uppercase text-muted-foreground">
                                  {t('modals.details.allergensLabel', { defaultValue: 'Allergens' })}
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  {allergens.map((tag) => (
                                    <Badge
                                      key={tag}
                                      variant="secondary"
                                      className="rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300"
                                    >
                                      {tag}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="mt-2 rounded-lg border border-dashed border-gray-200/60 bg-white/80 p-4 text-sm text-muted-foreground backdrop-blur-sm dark:border-gray-700/60 dark:bg-gray-900/40">
                      {t('modals.details.emptyItems')}
                    </div>
                  )}
                </div>
                {approvalRequestId ? (
                  <div className="space-y-4">
                    {approvalDetailQuery.isLoading ? (
                      <div className="rounded-lg border border-dashed border-muted-foreground/30 bg-muted/10 px-4 py-6 text-sm text-muted-foreground">
                        Loading approval workflow...
                      </div>
                    ) : approvalDetailQuery.isError ? (
                      <div className="rounded-lg border border-destructive/30 bg-destructive/8 px-4 py-3 text-sm text-destructive">
                        Unable to load approval detail. Try again shortly.
                      </div>
                    ) : approvalDetail ? (
                      <>
                        <DiscountApprovalSummaryCard
                          request={approvalDetail.request}
                          policy={approvalDetail.policy}
                          workflow={approvalDetail.workflow}
                          orderDiscount={selectedOrder.discount ?? null}
                        />
                        <DiscountApprovalTimeline
                          request={approvalDetail.request}
                          steps={approvalDetail.steps}
                          events={approvalDetail.events}
                        />
                        <div className="flex flex-wrap gap-2">
                          {pendingApprovalStep ? (
                            <>
                              <Button
                                className="flex-1 min-w-[140px] sm:flex-none"
                                disabled={approvalDecisionMutation.isPending || approvalDetailQuery.isFetching}
                                onClick={() => {
                                  if (!approvalDetail) {
                                    return;
                                  }
                                  setDecisionDialog({
                                    requestId: approvalDetail.request.id,
                                    stepId: pendingApprovalStep.id,
                                    decision: 'approve',
                                  });
                                  setDecisionNote('');
                                  setDecisionNoteError(null);
                                }}
                              >
                                Approve
                              </Button>
                              <Button
                                variant="destructive"
                                className="flex-1 min-w-[140px] sm:flex-none"
                                disabled={approvalDecisionMutation.isPending || approvalDetailQuery.isFetching}
                                onClick={() => {
                                  if (!approvalDetail) {
                                    return;
                                  }
                                  setDecisionDialog({
                                    requestId: approvalDetail.request.id,
                                    stepId: pendingApprovalStep.id,
                                    decision: 'decline',
                                  });
                                  setDecisionNote('');
                                  setDecisionNoteError(null);
                                }}
                              >
                                Decline
                              </Button>
                            </>
                          ) : null}
                          {canSubmitApproval ? (
                            <Button
                              variant="secondary"
                              className="flex-1 min-w-[140px] sm:flex-none"
                              disabled={approvalSubmitMutation.isPending}
                              onClick={() => {
                                setSubmitDialogOpen(true);
                                setSubmitNote('');
                              }}
                            >
                              Submit for review
                            </Button>
                          ) : null}
                          {canCancelApproval ? (
                            <Button
                              variant="outline"
                              className="flex-1 min-w-[140px] sm:flex-none"
                              disabled={approvalCancelMutation.isPending}
                              onClick={() => {
                                setCancelDialogOpen(true);
                                setCancelReason('');
                              }}
                            >
                              Cancel approval
                            </Button>
                          ) : null}
                        </div>
                      </>
                    ) : null}
                  </div>
                ) : null}
                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
                  <Button variant="outline" onClick={() => handleDetailsOpenChange(false)}>
                    {t('actions.close')}
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={!modalCanEdit}
                    onClick={() => selectedOrder && handleEditOrder(selectedOrder)}
                  >
                    {t('actions.edit', { defaultValue: 'Edit' })}
                  </Button>
                  <Button
                    variant="outline"
                    disabled={!modalCanRefund || modalRefunding}
                    onClick={() => selectedOrder && handleOpenRefundDialog(selectedOrder)}
                  >
                    {modalRefunding
                      ? t('actions.refund.pending', { defaultValue: 'Processing...' })
                      : t('actions.refund.label', { defaultValue: 'Refund guest' })}
                  </Button>
                  <Button
                    variant="destructive"
                    disabled={!modalCanCancel || modalCancelling}
                    onClick={() => selectedOrder && handleOpenCancelDialog(selectedOrder)}
                  >
                    {modalCancelling
                      ? t('actions.cancel.pending', { defaultValue: 'Cancelling...' })
                      : t('actions.cancel.label', { defaultValue: 'Cancel order' })}
                  </Button>
                  <Button
                    onClick={() => selectedOrder && handleMarkServed(selectedOrder)}
                    disabled={!modalCanServe || modalServing}
                  >
                    {modalServing ? t('actions.markServed.pending') : t('actions.markServed.label')}
                  </Button>
                </div>
              </div>
            ) : null}
          </SheetContent>
        </Sheet>
        <Dialog
          open={Boolean(cancelDialogOrder)}
          onOpenChange={(open) => {
            if (!open) {
              setCancelDialogOrder(null);
            }
          }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t('modals.cancel.title', { defaultValue: 'Cancel order' })}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {cancelDialogOrder
                  ? t('modals.cancel.description', {
                      code: cancelDialogOrder.code ?? cancelDialogOrder.id,
                      defaultValue: 'Move order {{code}} to Cancelled?',
                    })
                  : null}
              </p>
              {cancelOutstandingCents > 0 ? (
                <Alert className="border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-100">
                  <AlertTitle>
                    {t('modals.cancel.outstandingTitle', { defaultValue: 'Collect outstanding balance' })}
                  </AlertTitle>
                  <AlertDescription>
                    {t('modals.cancel.outstandingDescription', {
                      amount: cancelOutstandingLabel,
                      defaultValue: 'Collect {{amount}} before cancelling to avoid discrepancies.',
                    })}
                  </AlertDescription>
                </Alert>
              ) : null}
              {cancelRefundCents > 0 ? (
                <div className="space-y-3 rounded-2xl border border-destructive/30 bg-destructive/5 p-4">
                  <div>
                    <h4 className="text-sm font-semibold text-destructive">
                      {t('modals.cancel.refundTitle', { defaultValue: 'Refund guest before cancelling' })}
                    </h4>
                    <p className="text-xs text-destructive/80">
                      {t('modals.cancel.refundDescription', {
                        amount: cancelRefundLabel,
                        defaultValue: 'Return {{amount}} to the guest before cancelling this order.',
                      })}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-destructive/80">
                      {t('modals.cancel.paymentHeading', { defaultValue: 'Payment handling' })}
                    </p>
                    <p className="text-xs text-destructive/80">
                      {t('modals.cancel.paymentDescription', {
                        amount: cancelRefundLabel,
                        defaultValue: 'Choose how to record the {{amount}} refund so finance stays accurate.',
                      })}
                    </p>
                    <div className="space-y-2">
                      <label
                        htmlFor="cancel-payment-refund"
                        className={cn(
                          'flex cursor-pointer gap-3 rounded-2xl border p-3 transition',
                          cancelPaymentAction === 'refund'
                            ? 'border-destructive bg-background text-destructive ring-1 ring-destructive/60 dark:bg-transparent'
                            : 'border-destructive/50 bg-transparent hover:border-destructive/80',
                        )}
                      >
                        <input
                          id="cancel-payment-refund"
                          type="radio"
                          name="cancel-payment-action"
                          value="refund"
                          checked={cancelPaymentAction === 'refund'}
                          onChange={() => setCancelPaymentAction('refund')}
                          className="mt-1 h-4 w-4 border border-destructive/60 text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                        />
                        <div className="space-y-1">
                          <p className="text-sm font-semibold">
                            {t('modals.cancel.paymentOptions.refundTitle', {
                              amount: cancelRefundLabel,
                              defaultValue: 'Refund {{amount}} now',
                            })}
                          </p>
                          <p className="text-xs text-destructive/90">
                            {t('modals.cancel.paymentOptions.refundDescription', {
                              defaultValue: 'Record the refund immediately so dashboards and ledgers update everywhere.',
                            })}
                          </p>
                        </div>
                      </label>
                      <label
                        htmlFor="cancel-payment-retain"
                        className={cn(
                          'flex cursor-pointer gap-3 rounded-2xl border p-3 transition',
                          cancelPaymentAction === 'retain'
                            ? 'border-destructive bg-background text-destructive ring-1 ring-destructive/60 dark:bg-transparent'
                            : 'border-destructive/50 bg-transparent hover:border-destructive/80',
                        )}
                      >
                        <input
                          id="cancel-payment-retain"
                          type="radio"
                          name="cancel-payment-action"
                          value="retain"
                          checked={cancelPaymentAction === 'retain'}
                          onChange={() => setCancelPaymentAction('retain')}
                          className="mt-1 h-4 w-4 border border-destructive/60 text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                        />
                        <div className="space-y-1">
                          <p className="text-sm font-semibold">
                            {t('modals.cancel.paymentOptions.retainTitle', {
                              defaultValue: 'Keep funds / resolve later',
                            })}
                          </p>
                          <p className="text-xs text-destructive/90">
                            {t('modals.cancel.paymentOptions.retainDescription', {
                              defaultValue: 'Skip the refund for now. This order will continue to show a refund due until resolved.',
                            })}
                          </p>
                        </div>
                      </label>
                    </div>
                    {cancelPaymentAction === 'retain' && cancelRefundCents > 0 ? (
                      <Alert className="border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-100">
                        <AlertTitle>
                          {t('modals.cancel.paymentOptions.retainWarningTitle', {
                            defaultValue: 'Refund will remain outstanding',
                          })}
                        </AlertTitle>
                        <AlertDescription>
                          {t('modals.cancel.paymentOptions.retainWarningDescription', {
                            amount: cancelRefundLabel,
                            defaultValue: 'Order will continue to show {{amount}} as refund due until you record it.',
                          })}
                        </AlertDescription>
                      </Alert>
                    ) : null}
                  </div>
                </div>
              ) : null}
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
                <Button variant="outline" onClick={() => setCancelDialogOrder(null)}>
                  {t('actions.close')}
                </Button>
                <Button
                  variant="destructive"
                  disabled={cancelActionPending || !cancelDialogOrder}
                  onClick={() => void handleConfirmCancel()}
                >
                  {cancelActionPending
                    ? t('modals.cancel.processing', { defaultValue: 'Processing...' })
                    : t('actions.cancel.label', { defaultValue: 'Cancel order' })}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        <Dialog
          open={Boolean(refundDialogOrder)}
          onOpenChange={(open) => {
            if (!open) {
              setRefundDialogOrder(null);
              setRefundReason('');
            }
          }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t('modals.refund.title', { defaultValue: 'Refund guest' })}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {refundDialogOrder
                  ? t('modals.refund.description', {
                      amount: refundDialogLabel,
                      code: refundDialogOrder.code ?? refundDialogOrder.id,
                      defaultValue: 'Return {{amount}} to settle order {{code}}.',
                    })
                  : null}
              </p>
              <div className="space-y-2">
                <Label htmlFor="refund-note" className="text-xs uppercase text-muted-foreground">
                  {t('modals.refund.noteLabel', { defaultValue: 'Optional note' })}
                </Label>
                <Textarea
                  id="refund-note"
                  value={refundReason}
                  onChange={(event) => setRefundReason(event.target.value)}
                  placeholder={t('modals.refund.notePlaceholder', {
                    defaultValue: 'Add context for the refund (optional)',
                  })}
                />
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setRefundDialogOrder(null);
                    setRefundReason('');
                  }}
                >
                  {t('actions.close')}
                </Button>
                <Button
                  disabled={refundOrderMutation.isPending || !refundDialogOrder}
                  onClick={handleConfirmRefund}
                >
                  {refundOrderMutation.isPending
                    ? t('actions.refund.pending', { defaultValue: 'Processing...' })
                    : t('modals.refund.confirm', { defaultValue: 'Issue refund' })}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        <Dialog
          open={counterPeekOpen}
          onOpenChange={(open) => {
            setCounterPeekOpen(open);
            if (!open) {
              setCounterPeekKey((previous) => previous + 1);
            }
          }}
        >
          <DialogContent className="w-full max-w-5xl overflow-hidden border-0 bg-transparent p-0 shadow-none sm:rounded-3xl">
            <div className="flex h-[85vh] w-full flex-col overflow-hidden rounded-3xl bg-background shadow-2xl">
              <div className="flex items-center justify-between border-b border-border/60 px-6 py-4">
                <DialogTitle className="text-lg font-semibold text-foreground">
                  {t('actions.launchCounter')}
                </DialogTitle>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handlePopoutCounter}
                    aria-label={t('actions.openInNewTab', { defaultValue: 'Open in new tab' })}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setCounterPeekOpen(false)}
                    aria-label={t('actions.close')}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="flex-1 bg-muted/20">
                <iframe
                  key={counterPeekKey}
                  src={counterPeekUrl}
                  title={t('actions.launchCounter')}
                  className="h-full w-full border-0"
                  loading="lazy"
                  allow="payment"
                />
              </div>
            </div>
          </DialogContent>
        </Dialog>
        <Dialog
          open={Boolean(decisionDialog)}
          onOpenChange={(open) => {
            if (!open) {
              setDecisionDialog(null);
              setDecisionNote('');
              setDecisionNoteError(null);
            }
          }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {decisionDialog?.decision === 'approve' ? 'Approve discount' : 'Decline discount'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <Label htmlFor="decision-note" className="text-xs uppercase text-muted-foreground">
                {decisionDialog?.decision === 'approve'
                  ? 'Optional note'
                  : declineNoteRequired
                    ? 'Required note'
                    : 'Optional note'}
              </Label>
              <Textarea
                id="decision-note"
                value={decisionNote}
                onChange={(event) => {
                  setDecisionNote(event.target.value);
                  if (decisionNoteError) {
                    setDecisionNoteError(null);
                  }
                }}
                rows={4}
                placeholder={
                  decisionDialog?.decision === 'approve'
                    ? 'Add an optional note for the audit trail.'
                    : 'Add context for declining this request.'
                }
              />
              {decisionNoteError ? (
                <p className="text-xs text-destructive">{decisionNoteError}</p>
              ) : null}
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setDecisionDialog(null);
                  setDecisionNote('');
                  setDecisionNoteError(null);
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleDecisionConfirm} disabled={approvalDecisionMutation.isPending}>
                {approvalDecisionMutation.isPending
                  ? 'Saving...'
                  : decisionDialog?.decision === 'approve'
                    ? 'Approve'
                    : 'Decline'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        <Dialog
          open={submitDialogOpen}
          onOpenChange={(open) => {
            setSubmitDialogOpen(open);
            if (!open) {
              setSubmitNote('');
            }
          }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Submit for review</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <Label htmlFor="submit-note" className="text-xs uppercase text-muted-foreground">
                Optional message to approvers
              </Label>
              <Textarea
                id="submit-note"
                value={submitNote}
                onChange={(event) => setSubmitNote(event.target.value)}
                rows={3}
                placeholder="Share context for reviewers (optional)."
              />
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setSubmitDialogOpen(false);
                  setSubmitNote('');
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleSubmitApproval} disabled={approvalSubmitMutation.isPending}>
                {approvalSubmitMutation.isPending ? 'Submitting...' : 'Submit'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        <Dialog
          open={cancelDialogOpen}
          onOpenChange={(open) => {
            setCancelDialogOpen(open);
            if (!open) {
              setCancelReason('');
            }
          }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Cancel approval</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <Label htmlFor="cancel-reason" className="text-xs uppercase text-muted-foreground">
                Optional reason
              </Label>
              <Textarea
                id="cancel-reason"
                value={cancelReason}
                onChange={(event) => setCancelReason(event.target.value)}
                rows={3}
                placeholder="Let approvers know why you're cancelling (optional)."
              />
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setCancelDialogOpen(false);
                  setCancelReason('');
                }}
              >
                Keep request
              </Button>
              <Button
                variant="destructive"
                onClick={handleCancelApproval}
                disabled={approvalCancelMutation.isPending}
              >
                {approvalCancelMutation.isPending ? 'Cancelling...' : 'Cancel approval'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
  );
};

export default OrdersPageV3;
